import { ref } from 'vue'
import { defineStore } from 'pinia'
import api from '@/api'
import type { User } from '@/types/types'
import { usePusherStore } from '@/stores/pusher'
import { useStatusStore } from '@/stores/status'
import { useDeviceStore } from '@/stores/device'
import * as Sentry from '@sentry/vue'

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null)
  const pusherStore = usePusherStore()
  const statusStore = useStatusStore()
  const deviceStore = useDeviceStore()

  const logout = async () => {
    try {
      // Notify server of disconnect before logout
      await api.post('/api/v1/user/disconnect', { device: 'app' })

      await api.post('/api/v1/user/logout')

      if (user.value) {
        pusherStore?.pusher?.unsubscribe(`presence-app-${user.value.id}`)
      }

      user.value = null

      if (deviceStore.device) {
        deviceStore.device.destroy()
      }

      statusStore.status = 'login'

      Sentry.setUser(null)
    } catch (err) {
      console.log(err)
    }
  }

  const getUser = async () => {
    try {
      const response = await api.get('/api/v1/user?time=' + Math.floor(Date.now() / 1000))
      if (response.data.success) {
        user.value = response.data

        api.defaults.headers.common['X-CSRF-TOKEN'] = user?.value?.csrf_token

        api.interceptors.response.use(
          (response) => response,
          (error) => {
            if (
              error.response &&
              typeof error.response !== 'undefined' &&
              error.response.status === 401
            ) {
              location.reload()
            }
            return Promise.reject(error)
          },
        )
      } else {
        statusStore.status = 'login'
      }
    } catch (err) {
      console.log(err)
    }
  }

  const toggleDnd = async () => {
    if (!user.value) return
    try {
      const newValue = !user.value.donotdisturb
      await api.put('/api/v1/user', { donotdisturb: newValue })
      user.value.donotdisturb = newValue
    } catch (err) {
      console.log('toggleDnd error', err)
    }
  }

  return { user, logout, getUser, toggleDnd }
})
